import { ExclamationCircleIcon } from '@heroicons/react/outline';
import { Button } from 'components/Button';
import { Modal } from 'components/Modal';
import { useCallback, useState } from 'react';
import {
  unstable_useBlocker as useBlocker,
  useBeforeUnload,
} from 'react-router-dom';
import { SizeEnum } from 'utils';

type UnsavedChangesGuardProps = {
  hasUnsavedChanges: boolean;
  onSave: () => Promise<boolean>;
  saveBeforeLeaving?: boolean;
  message?: string;
};

export const UnsavedChangesGuard = ({
  hasUnsavedChanges,
  onSave,
  saveBeforeLeaving = true,
  message = 'Your changes have not been saved. Would you like to save them before leaving?',
}: UnsavedChangesGuardProps) => {
  const blocker = useBlocker(hasUnsavedChanges);
  const [isSaving, setIsSaving] = useState(false);

  useBeforeUnload(
    useCallback(
      (event) => {
        if (!hasUnsavedChanges) return;

        event.preventDefault();
        event.returnValue = '';
      },
      [hasUnsavedChanges],
    ),
    { capture: true },
  );

  const continueEditing = () => {
    if (isSaving || blocker.state !== 'blocked') return;
    blocker.reset();
  };

  const leaveWithoutSaving = () => {
    if (isSaving || blocker.state !== 'blocked') return;
    blocker.proceed();
  };

  const saveAndLeave = async () => {
    if (isSaving || blocker.state !== 'blocked') return;

    setIsSaving(true);
    const didSave = await onSave();
    setIsSaving(false);

    if (didSave) {
      blocker.proceed();
    }
  };

  return (
    <Modal
      open={blocker.state === 'blocked'}
      openModal={(open) => {
        if (!open) continueEditing();
      }}
      containerClass="w-full max-w-lg"
    >
      <div className="px-1 pb-1">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-amber-100">
            <ExclamationCircleIcon
              className="h-6 w-6 text-amber-600"
              aria-hidden="true"
            />
          </span>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              You have unsaved changes
            </h2>
            <p className="mt-2 text-sm leading-6 text-gray-600">{message}</p>
          </div>
        </div>

        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button
            size={SizeEnum.sm}
            variant="outlined"
            onClick={continueEditing}
            disabled={isSaving}
            className="justify-center"
          >
            Continue Editing
          </Button>
          <Button
            size={SizeEnum.sm}
            color="danger"
            variant="outlined"
            onClick={leaveWithoutSaving}
            disabled={isSaving}
            className="justify-center"
          >
            Leave Without Saving
          </Button>
          {saveBeforeLeaving && (
            <Button
              size={SizeEnum.sm}
              onClick={saveAndLeave}
              isLoading={isSaving}
              className="justify-center"
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};
